import { uuid, cardIdFor } from '../ids.js';
import { applyLocalMany, writeTx } from '../mutate.js';
import { getCardType, noteProblems, pickFields, sortText } from '../../cardTypes/index.js';
import { recomputeCards } from '../../scheduler/recompute.js';

/** A note that can't be saved as-is; `field` points at the input to fix. */
export class NoteError extends Error {
  constructor(message, field = null) {
    super(message);
    this.name = 'NoteError';
    this.field = field;
  }
}

function assertValid(type, fields) {
  const problems = noteProblems(type, fields);
  const [field, message] = Object.entries(problems)[0] ?? [];
  if (message) throw new NoteError(message, field === '_' ? null : field);
}

async function assertDeck(db, deckId) {
  const deck = deckId ? await db.decks.get(deckId) : null;
  if (!deck || deck.deletedAt) throw new NoteError('That deck no longer exists. Choose another one.', 'deckId');
}

function newCardPatch(noteId, deckId, ord, now) {
  return {
    noteId,
    deckId,
    ord,
    flag: 0,
    suspended: false,
    buriedUntil: null,
    deletedAt: null,
    createdAt: now,
    // device-local cache
    due: now,
    fsrs: null,
  };
}

export function normalizeTags(tags) {
  return [
    ...new Set(
      (tags ?? [])
        .map((t) => String(t).trim().replace(/\s+/g, '_'))
        .filter(Boolean),
    ),
  ].sort((a, b) => a.localeCompare(b));
}

/**
 * Create a note and its cards. Pass a stable `id` per draft: submitting the same
 * draft twice (double tap, retry after a hiccup) is then a no-op, never a duplicate.
 */
export async function createNote(db, { id = uuid(), deckId, type, fields: rawFields, tags = [] }) {
  const fields = pickFields(type, rawFields);
  assertValid(type, fields);
  const ords = getCardType(type).ords({ fields });
  if (!ords.length) throw new NoteError('This card has nothing to study yet.');
  return writeTx(db, async () => {
    if (await db.notes.get(id)) return id;
    await assertDeck(db, deckId);
    await insertNote(db, { id, deckId, type, fields, tags, ords });
    return id;
  });
}

async function insertNote(db, { id, deckId, type, fields, tags, ords }) {
  const now = Date.now();
  const ops = [
    {
      kind: 'note',
      id,
      patch: {
        deckId,
        type,
        fields,
        tags: normalizeTags(tags),
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
        purged: false,
      },
    },
    ...ords.map((ord) => ({
      kind: 'card',
      id: cardIdFor(id, ord),
      patch: newCardPatch(id, deckId, ord, now),
    })),
  ];
  await applyLocalMany(db, ops);
}

const normalizeKey = (s) => s.toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Live notes whose first field reads the same as this one (ignoring case and spacing).
 * Used to warn before adding the same card twice; never blocks saving.
 */
export async function findSimilarNotes(db, { type, fields, excludeId = null, limit = 3 }) {
  const key = normalizeKey(sortText({ type, fields }));
  if (key.length < 2) return [];
  const out = [];
  await db.notes
    .filter((n) => !n.deletedAt && !n.purged && n.id !== excludeId)
    .until(() => out.length >= limit)
    .each((n) => {
      if (normalizeKey(sortText(n)) === key) out.push(n);
    });
  return out;
}

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Update a note. Only fields that actually changed get new timestamps, so
 * concurrent edits of different fields on other devices are preserved.
 */
export async function updateNote(db, id, changes) {
  const now = Date.now();
  const touched = await writeTx(db, async () => {
    const note = await db.notes.get(id);
    if (!note || note.purged) throw new NoteError('This card no longer exists.');
    const nextType = changes.type ?? note.type;
    const known = new Set(getCardType(nextType).fields.map((f) => f.key));
    const incoming = Object.fromEntries(Object.entries(changes.fields ?? {}).filter(([k]) => known.has(k)));
    if (changes.fields) assertValid(nextType, { ...(note.fields ?? {}), ...incoming });
    if (changes.deckId && changes.deckId !== note.deckId) await assertDeck(db, changes.deckId);
    const patch = {};
    if (changes.fields) {
      const changed = {};
      for (const [k, v] of Object.entries(incoming)) {
        if (!same(note.fields?.[k], v)) changed[k] = v;
      }
      if (Object.keys(changed).length) patch.fields = changed;
    }
    if (changes.tags && !same(note.tags, normalizeTags(changes.tags))) {
      patch.tags = normalizeTags(changes.tags);
    }
    if (changes.type && changes.type !== note.type) patch.type = changes.type;
    if (changes.deckId && changes.deckId !== note.deckId) patch.deckId = changes.deckId;
    if (!Object.keys(patch).length) return [];
    patch.updatedAt = now;

    const next = {
      ...note,
      ...patch,
      fields: { ...(note.fields ?? {}), ...(patch.fields ?? {}) },
    };
    const deckId = next.deckId;
    const wanted = new Set(getCardType(next.type).ords(next));
    const cards = await db.cards.where('noteId').equals(id).toArray();
    const byOrd = new Map(cards.map((c) => [c.ord, c]));
    const ops = [{ kind: 'note', id, patch }];
    const recompute = [];

    for (const ord of wanted) {
      const card = byOrd.get(ord);
      if (!card) {
        ops.push({ kind: 'card', id: cardIdFor(id, ord), patch: newCardPatch(id, deckId, ord, now) });
      } else {
        const p = {};
        if (card.deletedAt) p.deletedAt = null;
        if (card.deckId !== deckId) p.deckId = deckId;
        if (Object.keys(p).length) {
          ops.push({ kind: 'card', id: card.id, patch: p });
          recompute.push(card.id);
        }
      }
    }
    for (const card of cards) {
      if (!wanted.has(card.ord) && !card.deletedAt) {
        ops.push({ kind: 'card', id: card.id, patch: { deletedAt: now } });
      }
    }
    await applyLocalMany(db, ops);
    return recompute;
  });
  if (touched.length) await recomputeCards(db, touched);
}

export async function deleteNotes(db, ids) {
  const now = Date.now();
  await writeTx(db, async () => {
    const cards = await db.cards.where('noteId').anyOf(ids).toArray();
    await applyLocalMany(db, [
      ...ids.map((id) => ({ kind: 'note', id, patch: { deletedAt: now } })),
      ...cards
        .filter((c) => !c.deletedAt)
        .map((c) => ({ kind: 'card', id: c.id, patch: { deletedAt: now } })),
    ]);
  });
}

export async function restoreNotes(db, ids) {
  await writeTx(db, async () => {
    const notes = (await db.notes.bulkGet(ids)).filter((n) => n?.deletedAt);
    const ops = [];
    for (const note of notes) {
      ops.push({ kind: 'note', id: note.id, patch: { deletedAt: null } });
      const cards = await db.cards.where('noteId').equals(note.id).toArray();
      for (const c of cards) {
        if (c.deletedAt === note.deletedAt) ops.push({ kind: 'card', id: c.id, patch: { deletedAt: null } });
      }
    }
    await applyLocalMany(db, ops);
  });
}

/** Permanently erase content (the tombstone stays so other devices learn about it). */
export async function purgeNotes(db, ids) {
  await writeTx(db, async () => {
    const notes = (await db.notes.bulkGet(ids)).filter(Boolean);
    await applyLocalMany(
      db,
      notes.map((n) => ({
        kind: 'note',
        id: n.id,
        patch: {
          purged: true,
          deletedAt: n.deletedAt ?? Date.now(),
          tags: [],
          fields: Object.fromEntries(Object.keys(n.fields ?? {}).map((k) => [k, null])),
        },
      })),
    );
  });
}

export async function moveNotes(db, ids, deckId) {
  const recompute = await writeTx(db, async () => {
    const cards = await db.cards.where('noteId').anyOf(ids).toArray();
    await applyLocalMany(db, [
      ...ids.map((id) => ({ kind: 'note', id, patch: { deckId, updatedAt: Date.now() } })),
      ...cards.map((c) => ({ kind: 'card', id: c.id, patch: { deckId } })),
    ]);
    return cards.map((c) => c.id);
  });
  await recomputeCards(db, recompute);
}

export async function changeTags(db, ids, { add = [], remove = [] }) {
  await writeTx(db, async () => {
    const notes = (await db.notes.bulkGet(ids)).filter(Boolean);
    const ops = [];
    for (const n of notes) {
      const set = new Set(n.tags ?? []);
      normalizeTags(add).forEach((t) => set.add(t));
      normalizeTags(remove).forEach((t) => set.delete(t));
      const tags = normalizeTags([...set]);
      if (!same(tags, n.tags)) ops.push({ kind: 'note', id: n.id, patch: { tags, updatedAt: Date.now() } });
    }
    await applyLocalMany(db, ops);
  });
}
